// Date helpers for the Journey page.
//
// The server derives every period bound and returns it in `period`, so nothing
// here computes a week or month boundary. These helpers only move the anchor
// date the toolbar sends, and lay dates out on a grid for rendering.
//
// The API speaks local time throughout — dates as 'YYYY-MM-DD', timestamps as
// wall-clock with no offset — so nothing here converts between zones.
//
// What it does guard against is JavaScript's own rule: a date-only string is
// parsed as UTC, so `new Date('2026-08-22')` is 8pm on the 21st in Toronto and
// every calendar cell lands on the wrong day. Every date string is therefore
// built from its parts at local noon, far enough from either edge that a DST
// shift cannot move it either. Do not replace this with `new Date(iso)`.

export const parseISODate = (iso) => {
	const [year, month, day] = iso.split('-').map(Number);
	return new Date(year, month - 1, day, 12, 0, 0);
};

const toISODate = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const todayISO = () => toISODate(new Date());

/**
 * Move the anchor date one period back or forward. The server turns whatever
 * day it receives into the surrounding week / month / year.
 *
 * @param {string} iso - Anchor date, YYYY-MM-DD
 * @param {'day'|'week'|'month'|'year'} view
 * @param {1|-1} step
 */
export const shiftAnchor = (iso, view, step) => {
	const date = parseISODate(iso);
	if (view === 'day') date.setDate(date.getDate() + step);
	else if (view === 'week') date.setDate(date.getDate() + 7 * step);
	else if (view === 'month') {
		// Anchor on the 1st so stepping off a 31st never skips a short month.
		date.setDate(1);
		date.setMonth(date.getMonth() + step);
	} else if (view === 'year') {
		date.setDate(1);
		date.setMonth(0);
		date.setFullYear(date.getFullYear() + step);
	}
	return toISODate(date);
};

/** Every date from start to end inclusive, as ISO strings. */
export const eachDay = (startISO, endISO) => {
	const days = [];
	const cursor = parseISODate(startISO);
	const end = parseISODate(endISO);
	while (cursor <= end) {
		days.push(toISODate(cursor));
		cursor.setDate(cursor.getDate() + 1);
	}
	return days;
};

export const daysBetween = (startISO, endISO) =>
	Math.round(
		(parseISODate(endISO) - parseISODate(startISO)) / (1000 * 60 * 60 * 24)
	) + 1;

/**
 * A month laid out as weeks of seven slots, Sunday first. Slots before the 1st
 * and after the last day are null so the grid keeps its shape.
 */
export const monthMatrix = (year, monthIndex) => {
	const first = new Date(year, monthIndex, 1, 12);
	const daysInMonth = new Date(year, monthIndex + 1, 0, 12).getDate();
	const leading = first.getDay();
	const slots = Array(leading).fill(null);
	for (let day = 1; day <= daysInMonth; day += 1) {
		slots.push(toISODate(new Date(year, monthIndex, day, 12)));
	}
	while (slots.length % 7 !== 0) slots.push(null);

	const weeks = [];
	for (let i = 0; i < slots.length; i += 7) weeks.push(slots.slice(i, i + 7));
	return weeks;
};

export const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const formatWeekdayShort = (iso) =>
	parseISODate(iso).toLocaleDateString('en-US', { weekday: 'short' });

export const formatDayOfMonth = (iso) => parseISODate(iso).getDate();

export const formatMonthName = (monthIndex) =>
	new Date(2000, monthIndex, 1).toLocaleDateString('en-US', { month: 'long' });

export const formatFullDate = (iso) =>
	parseISODate(iso).toLocaleDateString('en-US', {
		weekday: 'long',
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	});

export const formatMediumDate = (iso) =>
	parseISODate(iso).toLocaleDateString('en-US', {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
	});

/** '07:35:00' → '7:35 AM' */
export const formatTime = (time) => {
	if (!time) return '';
	const [hours, minutes] = time.split(':').map(Number);
	const suffix = hours < 12 ? 'AM' : 'PM';
	const hour12 = hours % 12 === 0 ? 12 : hours % 12;
	return `${hour12}:${String(minutes).padStart(2, '0')} ${suffix}`;
};

// The day timeline runs 6am → 6am, matching the morning/afternoon/evening/night
// buckets: minute 0 is 6am, minute 1440 is 6am the next day.
const DAY_TIMELINE_START_HOUR = 6;
const MINUTES_IN_DAY = 1440;

export const minutesSinceDayStart = (time) => {
	if (!time) return 0;
	const [hours, minutes] = time.split(':').map(Number);
	const raw = hours * 60 + minutes - DAY_TIMELINE_START_HOUR * 60;
	return ((raw % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
};

/**
 * The current local time as 'HH:MM' — the value an <input type="time"> holds.
 * Seconds are dropped: the control has no place to show them, and the rating
 * log only ever displays down to the minute.
 */
export const nowTimeValue = () => {
	const now = new Date();
	return `${String(now.getHours()).padStart(2, '0')}:${String(
		now.getMinutes()
	).padStart(2, '0')}`;
};
